// Synchronized Countdown Service
import { supabase, isSupabaseConfigured } from '../config/supabase';
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

/**
 * Start a synchronized countdown for a movie
 */
export const startSynchronizedCountdown = async (movieId, startedBy = null) => {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured, cannot start synchronized countdown');
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    console.log('🚀 Starting synchronized countdown for movie:', movieId);
    
    // First, deactivate any existing countdowns for this movie
    console.log('🧹 Deactivating existing countdowns for movie:', movieId);
    const deactivateResult = await queuedSupabaseOperation.update(
      'countdown_state',
      { is_active: false },
      `movie_id=eq.${encodeURIComponent(movieId)}&is_active=eq.true`,
      null,
      'Deactivate existing countdowns'
    );
    console.log('🧹 Deactivation result:', deactivateResult);
    
    // Create new countdown starting at second 1
    const expiresAt = new Date();
    expiresAt.setSeconds(expiresAt.getSeconds() + 15); // 10 seconds counting + 2 seconds GOOOO + 3 seconds buffer
    
    const countdownData = {
      movie_id: movieId,
      seconds: 1,
      phase: 'counting',
      is_active: true,
      started_by: startedBy,
      expires_at: expiresAt.toISOString()
    };
    
    const result = await queuedSupabaseOperation.insert(
      'countdown_state',
      countdownData,
      null,
      'Start synchronized countdown'
    );
    
    console.log('✅ Synchronized countdown started:', result);
    return { data: result, error: null };
    
  } catch (error) {
    console.error('❌ Error starting synchronized countdown:', error);
    return { data: null, error };
  }
};

/**
 * Update countdown state (increment seconds, change phase)
 */
export const updateCountdownState = async (movieId, seconds, phase = 'counting') => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    console.log('⏰ Updating countdown state:', { movieId, seconds, phase });
    
    const result = await queuedSupabaseOperation.update(
      'countdown_state',
      { 
        seconds: seconds,
        phase: phase
      },
      `movie_id=eq.${encodeURIComponent(movieId)}&is_active=eq.true`,
      null,
      `Update countdown: ${seconds}s (${phase})`
    );
    
    return { data: result, error: null };
    
  } catch (error) {
    console.error('❌ Error updating countdown state:', error);
    return { data: null, error };
  }
};

/**
 * Stop/deactivate countdown for a movie
 */
export const stopSynchronizedCountdown = async (movieId) => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    console.log('🛑 Stopping synchronized countdown for movie:', movieId);
    
    const result = await queuedSupabaseOperation.update(
      'countdown_state',
      { is_active: false },
      `movie_id=eq.${encodeURIComponent(movieId)}&is_active=eq.true`,
      null,
      'Stop synchronized countdown'
    );
    
    console.log('✅ Synchronized countdown stopped');
    return { data: result, error: null };
    
  } catch (error) {
    console.error('❌ Error stopping synchronized countdown:', error);
    return { data: null, error };
  }
};

/**
 * Get current active countdown for a movie
 */
export const getActiveCountdown = async (movieId) => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const result = await queuedSupabaseOperation.select(
      'countdown_state',
      `select=*&movie_id=eq.${encodeURIComponent(movieId)}&is_active=eq.true&expires_at=gt.${new Date().toISOString()}&order=started_at.desc&limit=1`,
      null,
      'Get active countdown'
    );
    
    const countdown = result && result.length > 0 ? result[0] : null;
    return { data: countdown, error: null };
    
  } catch (error) {
    console.error('❌ Error getting active countdown:', error);
    return { data: null, error };
  }
};

/**
 * Get all active countdowns (for admin monitoring)
 */
export const getAllActiveCountdowns = async () => {
  if (!isSupabaseConfigured()) {
    return { data: [], error: 'Supabase not configured' };
  }

  try {
    const result = await queuedSupabaseOperation.select(
      'countdown_state',
      `select=*&is_active=eq.true&expires_at=gt.${new Date().toISOString()}&order=started_at.desc`,
      null,
      'Get all active countdowns'
    );
    
    return { data: result || [], error: null };
    
  } catch (error) {
    console.error('❌ Error getting active countdowns:', error);
    return { data: [], error };
  }
};

/**
 * Subscribe to countdown changes for real-time updates
 */
export const subscribeToCountdownChanges = (movieId, callback) => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured, cannot subscribe to countdown changes');
    return null;
  }

  console.log('👂 Subscribing to countdown changes for movie:', movieId);
  
  const subscription = supabase
    .channel(`countdown-${movieId}`)
    .on(
      'postgres_changes',
      {
        event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
        schema: 'public',
        table: 'countdown_state',
        filter: `movie_id=eq.${movieId}`
      },
      (payload) => {
        console.log('🔄 Countdown change received for movie', movieId, ':', payload);
        console.log('🔍 Payload details:', {
          eventType: payload.eventType,
          new: payload.new,
          old: payload.old,
          table: payload.table,
          schema: payload.schema
        });
        
        // Only process active countdowns that haven't expired
        const countdown = payload.new;
        if (countdown && countdown.is_active && new Date(countdown.expires_at) > new Date()) {
          console.log('✅ Processing active countdown:', countdown);
          callback({
            movieId: countdown.movie_id,
            seconds: countdown.seconds,
            phase: countdown.phase,
            startedAt: countdown.started_at,
            expiresAt: countdown.expires_at
          });
        } else if (payload.eventType === 'DELETE' || (countdown && !countdown.is_active)) {
          console.log('🛑 Countdown stopped or deleted');
          // Countdown was stopped or deleted
          callback(null);
        } else {
          console.log('⚠️ Ignoring countdown event - not active or expired');
        }
      }
    )
    .subscribe((status) => {
      console.log('📡 Countdown subscription status for movie', movieId, ':', status);
      if (status === 'SUBSCRIBED') {
        console.log('✅ Countdown subscription is active and listening for changes');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('❌ Countdown subscription channel error for movie', movieId);
      } else if (status === 'TIMED_OUT') {
        console.error('❌ Countdown subscription timed out for movie', movieId);
      } else if (status === 'CLOSED') {
        console.error('❌ Countdown subscription closed for movie', movieId);
      }
    });

  return subscription;
};

/**
 * Unsubscribe from countdown changes
 */
export const unsubscribeFromCountdownChanges = (subscription) => {
  if (subscription && supabase) {
    console.log('🔇 Unsubscribing from countdown changes');
    supabase.removeChannel(subscription);
  }
};

/**
 * Clean up expired countdowns (utility function)
 */
export const cleanupExpiredCountdowns = async () => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    console.log('🧹 Cleaning up expired countdowns...');
    
    const result = await queuedSupabaseOperation.update(
      'countdown_state',
      { is_active: false },
      `is_active=eq.true&expires_at=lt.${new Date().toISOString()}`,
      null,
      'Clean up expired countdowns'
    );
    
    console.log('✅ Expired countdowns cleaned up');
    return { data: result, error: null };
    
  } catch (error) {
    console.error('❌ Error cleaning up expired countdowns:', error);
    return { data: null, error };
  }
};

